import { isTreeScrollable } from './IsScrollable';
import PullingContent from './PullingContent';
import RefreshingContent from './RefreshingContent';
import { DIRECTION } from './Types';
import './ptr.scss';

import React, { useRef, useEffect, JSX } from 'react';


interface PullToRefreshProps {
    isPullable?: boolean;
    onRefresh: () => Promise<any>;
    refreshingContent?: JSX.Element | string;
    pullingContent?: JSX.Element | string;
    children: JSX.Element;
    pullDownThreshold?: number;
    maxPullDownDistance?: number;
    resistance?: number;
    backgroundColor?: string;
    className?: string;
}

const PullToRefresh: React.FC<PullToRefreshProps> = ({
    isPullable = true,
    onRefresh,
    refreshingContent = <RefreshingContent />,
    pullingContent = <PullingContent />,
    children,
    pullDownThreshold = 67,
    maxPullDownDistance = 95,
    resistance = 1,
    backgroundColor,
    className = '',
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const childrenRef = useRef<HTMLDivElement>(null);
    const pullDownRef = useRef<HTMLDivElement>(null);
    
    let pullToRefreshThresholdBreached: boolean = false;
    let isDragging: boolean = false;
    let startY: number = 0;
    let currentY: number = 0;

    const excludedClassnames = ['googlemap-container']; 

    useEffect(() => {
        if (!isPullable || !childrenRef || !childrenRef.current) return;

        const childrenEl = childrenRef.current;

        childrenEl.addEventListener('touchstart', onTouchStart, { passive: true });
        childrenEl.addEventListener('mousedown', onTouchStart);
        childrenEl.addEventListener('touchmove', onTouchMove, { passive: false });
        childrenEl.addEventListener('mousemove', onTouchMove);

        childrenEl.addEventListener('touchend', onEnd);
        childrenEl.addEventListener('mouseup', onEnd);

        document.body.addEventListener('mouseleave', onEnd);

        return () => {
            childrenEl.removeEventListener('touchstart', onTouchStart);
            childrenEl.removeEventListener('mousedown', onTouchStart);
            childrenEl.removeEventListener('touchmove', onTouchMove);
            childrenEl.removeEventListener('mousemove', onTouchMove);

            childrenEl.removeEventListener('touchend', onEnd);
            childrenEl.removeEventListener('mouseup', onEnd);
            document.body.removeEventListener('mouseleave', onEnd);
        };
    }, [
        children,
        isPullable,
        onRefresh,
        pullDownThreshold,
        maxPullDownDistance,
    ]);

    const handleRefresh = () => {
        return new Promise(res => {
            setTimeout(() => {
                res(onRefresh());
            }, 125);
        });
    };


    const initContainer = (): void => {
        requestAnimationFrame(() => {
            /**
             * Reset Styles
             */
            if (childrenRef.current) {
                childrenRef.current.style.overflowX = 'hidden';
                childrenRef.current.style.overflowY = 'auto';
                childrenRef.current.style.transform = `unset`;
            }
            if (pullDownRef.current) {
                pullDownRef.current.style.opacity = '0';
            }
            if (containerRef.current) {
                containerRef.current.classList.remove('ptr--pull-down-treshold-breached');
                containerRef.current.classList.remove('ptr--dragging');
                containerRef.current.classList.remove('ptr--fetch-more-treshold-breached');
            }

            if (pullToRefreshThresholdBreached) pullToRefreshThresholdBreached = false;
        });
    };

    const onTouchStart = (e: MouseEvent | TouchEvent): void => {
        isDragging = false;

        // Check if the event target or its ancestors have any of the excluded classnames
        let targetElement = e.target as HTMLElement;
        while (targetElement) {
            if (
                targetElement.classList &&
                excludedClassnames.some(classname => targetElement.classList.contains(classname))
            ) {
                return; // Exit early if the pull starts from an excluded classname
            }
            targetElement = targetElement.parentElement as HTMLElement;
        }

        if (e instanceof MouseEvent) {
            startY = e.pageY;
        }
        if (window.TouchEvent && e instanceof TouchEvent) {
            startY = e.touches[0].pageY;
        }
        currentY = startY;

        // Check if the container is scrolled to the top
        if (childrenRef.current && childrenRef.current.scrollTop > 0) {
            return; // Prevent pull-to-refresh if not at the top
        }

        // Check if element can be scrolled
        if (e.type === 'touchstart' && isTreeScrollable(e.target as HTMLElement, DIRECTION.UP)) {
            return;
        }
        // Top non visible so cancel
        if (childrenRef.current!.getBoundingClientRect().top < 0) {
            return;
        }
        isDragging = true;
    };

    const onTouchMove = (e: MouseEvent | TouchEvent): void => {
        if (!isDragging) {
            return;
        }

        if (window.TouchEvent && e instanceof TouchEvent) {
            currentY = e.touches[0].pageY;
        } else {
            currentY = (e as MouseEvent).pageY;
        }

        containerRef.current!.classList.add('ptr--dragging');

        if (currentY < startY) {
            isDragging = false;
            return;
        }

        if (e.cancelable) {
            e.preventDefault();
        }

        const yDistanceMoved = Math.min((currentY - startY) / resistance, maxPullDownDistance);

        // Limit to trigger refresh has been breached
        if (yDistanceMoved >= pullDownThreshold) {
            isDragging = true;
            pullToRefreshThresholdBreached = true;
            containerRef.current!.classList.remove('ptr--dragging');
            containerRef.current!.classList.add('ptr--pull-down-treshold-breached');
        }

        // maxPullDownDistance breached, stop the animation
        if (yDistanceMoved >= maxPullDownDistance) {
            return;
        }

        pullDownRef.current!.style.opacity = ((yDistanceMoved) / 65).toString();
        childrenRef.current!.style.overflow = 'visible';
        childrenRef.current!.style.transform = `translate(0px, ${yDistanceMoved}px)`;
        pullDownRef.current!.style.visibility = 'visible';
    };

    const onEnd = (): void => {
        isDragging = false;
        startY = 0;
        currentY = 0;

        // Container has not been dragged enough, put it back to it's initial state
        if (!pullToRefreshThresholdBreached) {
            if (pullDownRef.current) pullDownRef.current.style.visibility = 'hidden';
            initContainer();
            return;
        }

        if (childrenRef.current) {
            childrenRef.current.style.overflow = 'visible';
            childrenRef.current.style.transform = `translate(0px, ${pullDownThreshold}px)`;
        }
        handleRefresh().then(initContainer).catch(initContainer);
    };

    return (
        <div className={`ptr ${className}`} style={{ backgroundColor }} ref={containerRef}>
            <div className="ptr__pull-down" ref={pullDownRef}>
                <div className="ptr__loader ptr__pull-down--loading">{refreshingContent}</div>
                <div className="ptr__pull-down--pull-more">{pullingContent}</div>
            </div>
            <div className="ptr__children" ref={childrenRef}>
                {children}
            </div>
        </div>
    );
};

export default PullToRefresh;
